import React, { useState, useEffect, useContext, useRef } from "react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import * as turf from "@turf/turf";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  Polygon,
} from "@react-google-maps/api";
import { HiOutlineArrowLeft } from "react-icons/hi";
import { RiErrorWarningFill } from "react-icons/ri";
import {
  Camera,
  CameraResultType,
  CameraSource,
  CameraDirection,
} from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";
import CameraPreviewComponent from "./Component/CameraPreviewComponent";
import { Capacitor } from "@capacitor/core";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import * as attenadanceService from "../../Services/attendanceService";
import * as siteService from "../../Services/siteService";
import { getUser } from "../../Services/UserService";
import { BASE, PROFILE } from "../../routes";
import { parseWKTPolygon } from "../../Utils/geoUtils";
import { formatTime, formatTimeAMPM } from "../../Utils/timeUtils";
import { Loader } from "../../Components/Common";
import {
  ATTENDANCE_TYPES,
  IN_PROGRESS,
  INCOMPLETE_TASK_WARNING,
  MESSAGES,
  PENDING,
  TASK_ERROR,
  TASK_WARNING,
} from "./constants";
import { GET_ALL_PAGINATION } from "../../Constants/Strings";
import { getTask } from "../../Services/taskServices";
import { UserContext } from "../../Context";
import { HiViewfinderCircle } from "react-icons/hi2";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const validationSchema = Yup.object().shape({
  date: Yup.string().required(MESSAGES.DATE_REQUIRED),
  time: Yup.string().required(MESSAGES.TIME_REQUIRED),
});
const libraries = ["places"];

const AttendanceForm = ({ type }) => {
  const [photo, setPhoto] = useState(null);
  const [siteOptions, setSiteOptions] = useState([]);
  const [location, setLocation] = useState({ latitude: null, longitude: null, accuracy: null });
  const [refreshingLocation, setRefreshingLocation] = useState(false);
  const [timeLeft, setTimeLeft] = useState(5 * 60);
  const [selectedSitePolygon, setSelectedSitePolygon] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [outsideGeofence, setOutsideGeofence] = useState(false);
  const [hasTodayTask, setHasTodayTask] = useState(false); // null = not checked yet
  const { isAdminOrHO } = useContext(UserContext);
  const containerStyle = { width: "100%", height: "300px" };
  const navigate = useNavigate();
  const isAndroid = Capacitor.getPlatform() === "android";
  const [taskWarning, setTaskWarning] = useState(false);
  const [taskCheckLoading, setTaskCheckLoading] = useState(true);
  const [loadingComplete, setLoadingComplete] = useState(false);
  const [siteLoaded, setSiteLoaded] = useState(false);
  const [locationLoaded, setLocationLoaded] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [profile, setProfile] = useState(null);
  const [profileLoaded, setProfileLoaded] = useState(false);
  const [resolvedSite, setResolvedSite] = useState(null);
  const [locationError, setLocationError] = useState(null); // 'permission-denied' | 'services-disabled' | 'timeout' | null
  const isLocationAvailable = Boolean(location.latitude && location.longitude);
  const isMountedRef = useRef(true);
  const isNativePlatform = Capacitor.isNativePlatform();

  const LOCATION_MAX_RETRIES = 3;
  const LOCATION_RETRY_DELAY = 3000; // ms
  // Geofence margin grows with the phone's reported GPS error, within these bounds
  const GEOFENCE_MIN_BUFFER = 30; // meters
  const GEOFENCE_MAX_BUFFER = 100; // meters

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const checkUserInsideGeofence = (coords) => {
    if (!coords || coords.length < 3 || !isLocationAvailable) {
      return false;
    }
    const userPoint = turf.point([location.longitude, location.latitude]);
    const sitePolygon = turf.polygon([coords]);
    // A reading with ±60 m accuracy can land 60 m away from where the user
    // actually stands, so don't block on-site users for normal GPS error.
    const buffer = Math.min(
      Math.max(GEOFENCE_MIN_BUFFER, location.accuracy || 0),
      GEOFENCE_MAX_BUFFER,
    );
    const bufferedPolygon = turf.buffer(sitePolygon, buffer, {
      units: "meters",
    });
    return turf.booleanPointInPolygon(userPoint, bufferedPolygon);
  };

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    libraries,
  });

  const initialAttendanceValue = {
    date: new Date().toISOString().split("T")[0],
    time: new Date().toLocaleTimeString("en-GB", {
      hour: "2-digit",
      minute: "2-digit",
    }),
    site: profile?.default_site || "",
    mark_attendance_without_site: false,
  };

  const takePhoto = async () => {
    try {
      const photo = await Camera.getPhoto({
        resultType: CameraResultType.Uri,
        source: CameraSource.Camera,
        quality: 70,
        cameraDirection: CameraDirection.Front,
        width: 640,
        height: 800,
        preserveAspectRatio: true,
      });

      const response = await fetch(photo.webPath);
      const blob = await response.blob();
      const file = new File([blob], "selfie.jpg", { type: blob.type });
      setPhoto(file);
    } catch (error) {
      toast.error("Camera Error:", error);
    }
  };

  // Resolve the location permission state before ever calling
  // getCurrentPosition. This lets us tell a genuinely denied permission or
  // disabled GPS apart from a transient fix timeout, instead of silently
  // retrying a call that can never succeed and then blaming "GPS".
  //
  // - Native (Android/iOS): checkPermissions() rejects specifically when
  //   system location services are switched off; otherwise it resolves with
  //   'granted' | 'denied' | 'prompt', and requestPermissions() surfaces the
  //   OS permission dialog when still 'prompt'.
  // - Web: requestPermissions() is unimplemented by the Capacitor web
  //   plugin, and the Permissions API isn't available in every browser, so
  //   we fall back to letting getCurrentPosition trigger the browser's own
  //   permission prompt directly.
  const resolveLocationPermission = async () => {
    try {
      const status = await Geolocation.checkPermissions();
      if (status.location === "denied") return "denied";
      if (status.location === "granted") return "granted";

      if (isNativePlatform) {
        const requested = await Geolocation.requestPermissions();
        return requested.location;
      }
      return "prompt";
    } catch (err) {
      if (isNativePlatform) {
        // Native plugin rejects checkPermissions specifically when system
        // location services (GPS) are turned off.
        throw err;
      }
      // Browser without the Permissions API (e.g. older Safari) — fall back
      // to getCurrentPosition, which will prompt on its own.
      return "prompt";
    }
  };

  const getGeoLocation = async () => {
    setLocationError(null);

    let permissionState;
    try {
      permissionState = await resolveLocationPermission();
    } catch (err) {
      if (isMountedRef.current) {
        setLocationError("services-disabled");
        setLocationLoaded(true);
      }
      return;
    }

    if (permissionState === "denied") {
      if (isMountedRef.current) {
        setLocationError("permission-denied");
        setLocationLoaded(true);
      }
      return;
    }

    let attempts = 0;

    while (attempts <= LOCATION_MAX_RETRIES) {
      if (!isMountedRef.current) return;

      try {
        const position = await Geolocation.getCurrentPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });

        if (!isMountedRef.current) return;

        setLocation({
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
        });

        setLocationLoaded(true);
        return;
      } catch (error) {
        attempts += 1;

        if (attempts > LOCATION_MAX_RETRIES) {
          if (isMountedRef.current) {
            setLocationError("timeout");
            setLocationLoaded(true);
          }
          return;
        }

        // wait before retry
        await new Promise((res) => setTimeout(res, LOCATION_RETRY_DELAY));
      }
    }
  };

  const retryLocation = () => {
    setLocationError(null);
    setLocationLoaded(false);
    getGeoLocation();
  };

  // Takes a fresh GPS reading without leaving the page, e.g. after the user
  // moves to an open area when first marked outside the geofence.
  const refreshLocation = async () => {
    if (refreshingLocation) return;
    setRefreshingLocation(true);
    await getGeoLocation();
    if (isMountedRef.current) setRefreshingLocation(false);
  };

  // Only judge the geofence once we actually have a fix — evaluating this
  // while location is still being fetched used to fire prematurely (the
  // default site's polygon loads faster than GPS does) and kick the user
  // out with a false "Location Not available" alert.
  useEffect(() => {
    if (!selectedSitePolygon || !isLocationAvailable) return;

    const isInside = checkUserInsideGeofence(selectedSitePolygon);
    setOutsideGeofence(!isInside);
    // Re-run on every new reading so a refresh can clear a stale "outside" result
  }, [selectedSitePolygon, location]);

  const handleSubmit = async (values) => {
    if (submitting) return;
    setSubmitting(true);

    const markWithoutSite =
      Boolean(profile?.mark_attendance_without_site) &&
      Boolean(values.mark_attendance_without_site);
    const selectedSite = markWithoutSite ? null : resolvedSite;

    if (!selectedSite && !markWithoutSite) {
      toast.error(MESSAGES.NO_DEFAULT_SITE_BODY);
      setSubmitting(false);
      return;
    }

    if (!photo) {
      setSubmitting(false);
      toast.error(MESSAGES.SELFIE_REQUIRED);
      return;
    }

    if (!location.latitude || !location.longitude) {
      toast.error(MESSAGES.LOCATION_REQUIRED);
      setSubmitting(false);
      return;
    }

    if (selectedSite && outsideGeofence) {
      toast.error(
        "You must be within your default site's location to mark attendance.",
      );
      setSubmitting(false);
      return;
    }

    const formData = new FormData();

    if (selectedSite) {
      formData.append("site", selectedSite.id);
    }
    formData.append("date", values.date);
    formData.append("is_backdated", 0);
    formData.append("type", type);
    formData.append("time", values.time);
    formData.append("photo", photo);
    formData.append("latitude", location.latitude);
    formData.append("longitude", location.longitude);

    try {
      const response = await attenadanceService.markAttenadance(formData);
      if (response.status === 201) {
        if (type === ATTENDANCE_TYPES.CHECKIN) {
          const checkinTime = response.data.checkin_time;
          toast.success(
            MESSAGES.SUBMITTED_ATTENDANCE(formatTimeAMPM(checkinTime)),
          );
        }
        if (type === ATTENDANCE_TYPES.CHECKOUT) {
          const checkoutTime = response.data.checkout_time;
          toast.success(
            MESSAGES.SUBMITTED_ATTENDANCE(formatTimeAMPM(checkoutTime)),
          );
        }
        navigate(BASE);
      } else {
        const msg = extractErrorMessage(response) || MESSAGES.FAILED_SUBMISSION;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || MESSAGES.FAILED_SUBMISSION;
      toast.error(msg);
    } finally {
      setSubmitting(false);
      setTaskWarning(false);
    }
  };

  useEffect(() => {
    const fetchSites = async () => {
      try {
        const response = await siteService.getAssignedSites(GET_ALL_PAGINATION);
        setSiteOptions(response.data.results);
        setSiteLoaded(true);
      } catch (error) {
        const msg = extractErrorMessage(error) || MESSAGES.SITES_ERROR;
        toast.error(msg);
      }
    };

    const fetchProfile = async () => {
      try {
        const response = await getUser();
        if (response.status === 200) {
          setProfile(response.data);
        }
      } catch (error) {
        const msg = extractErrorMessage(error) || MESSAGES.USER_DATA_ERROR;
        toast.error(msg);
      } finally {
        setProfileLoaded(true);
      }
    };

    fetchSites();
    fetchProfile();
    getGeoLocation();
  }, []);

  // Resolve the user's default site (and its geofence) once both the fresh
  // profile and the assigned-sites list have loaded. Attendance is always
  // marked against this site — there is no manual site picker anymore.
  useEffect(() => {
    if (!profileLoaded || !siteLoaded) return;

    const defaultSiteId = profile?.default_site;
    if (!defaultSiteId) {
      setResolvedSite(null);
      setSelectedSitePolygon(null);
      setOutsideGeofence(false);
      return;
    }

    const site = siteOptions.find(
      (s) => String(s.id) === String(defaultSiteId),
    );
    setResolvedSite(site || null);

    if (site?.geofence) {
      setSelectedSitePolygon(parseWKTPolygon(site.geofence));
    } else {
      setSelectedSitePolygon(null);
      setOutsideGeofence(false);
    }
  }, [profileLoaded, siteLoaded, profile, siteOptions]);

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev > 0) {
          return prev - 1;
        } else {
          clearInterval(timer);
          return 0;
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (timeLeft === 0) {
      navigate(BASE);
    }
  }, [timeLeft]);

  // Note: Google Maps' `isLoaded` is intentionally NOT a condition here. The
  // map is only a visual aid (marker + geofence preview); gating the whole
  // check-in flow on it meant a missing/invalid REACT_APP_GOOGLE_MAPS_API_KEY
  // (the "NoApiKeys" failure) left the form stuck on this loading screen
  // forever, even when GPS and the default site were both ready.
  useEffect(() => {
    if (
      siteLoaded &&
      profileLoaded &&
      locationLoaded &&
      (isAdminOrHO || !taskCheckLoading)
    ) {
      setLoadingComplete(true);
    }
  }, [
    siteLoaded,
    profileLoaded,
    locationLoaded,
    taskCheckLoading,
    isAdminOrHO,
  ]);

  const verifyTaskForToday = async () => {
    const todayDate = new Date().toISOString().split("T")[0];
    try {
      const response = await getTask({ start_date: todayDate });
      if (response.status === 200 && response.data.results.length > 0) {
        setHasTodayTask(true);

        const hasPendingOrInProgress = response.data.results.some(
          (task) => task.status === PENDING || task.status === IN_PROGRESS,
        );

        setTaskWarning(hasPendingOrInProgress);
      } else {
        setHasTodayTask(false);
        setTaskWarning(false);
      }
    } catch (err) {
      const msg = extractErrorMessage(err) || TASK_ERROR;
      toast.error(msg);
      setHasTodayTask(false);
      setTaskWarning(false);
    } finally {
      setTaskCheckLoading(false);
    }
  };

  useEffect(() => {
    verifyTaskForToday();
  }, []);

  if (!loadingComplete || !locationLoaded) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-3 px-4 text-center">
        <Loader />
        {!locationLoaded ? (
          <>
            <p className="text-sm font-medium text-gray-700 animate-pulse">
              Fetching your location…
            </p>
            <p className="text-xs text-gray-500 max-w-xs">
              Please keep GPS enabled and stay at your location.
            </p>
          </>
        ) : (
          <p className="text-sm text-gray-600 animate-pulse">
            Preparing attendance form…
          </p>
        )}
      </div>
    );
  }

  if (locationError) {
    const errorCopy = {
      "permission-denied": MESSAGES.LOCATION_PERMISSION_DENIED,
      "services-disabled": MESSAGES.LOCATION_SERVICES_DISABLED,
      timeout: MESSAGES.LOCATION_REQUIRED,
    };

    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-3 px-6 text-center">
        <RiErrorWarningFill className="w-12 h-12 text-red-500" />
        <h2 className="text-xl font-bold text-primary">Location Unavailable</h2>
        <p className="text-sm text-gray-600 max-w-sm">
          {errorCopy[locationError] || MESSAGES.LOCATION_REQUIRED}
        </p>
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={() => navigate(BASE)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={retryLocation}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-green-700"
          >
            {MESSAGES.RETRY_LOCATION}
          </button>
        </div>
      </div>
    );
  }

  if (!resolvedSite && !profile?.mark_attendance_without_site) {
    return (
      <div className="h-screen w-full flex flex-col items-center justify-center gap-3 px-6 text-center">
        <RiErrorWarningFill className="w-12 h-12 text-red-500" />
        <h2 className="text-xl font-bold text-primary">
          {MESSAGES.NO_DEFAULT_SITE_TITLE}
        </h2>
        <p className="text-sm text-gray-600 max-w-sm">
          {MESSAGES.NO_DEFAULT_SITE_BODY}
        </p>
        <div className="flex gap-3 mt-2">
          <button
            type="button"
            onClick={() => navigate(BASE)}
            className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => navigate(PROFILE)}
            className="bg-primary text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Go to Profile
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto py-2 px-6 rounded-lg flex justify-center items-center">
      <div className="bg-white rounded-lg p-6 pt-4 w-full max-w-3xl shadow-md">
        <div className="relative flex items-center justify-center h-10 mb-2">
          <button
            type="button"
            onClick={() => navigate(BASE)}
            className="absolute left-0 text-primary hover:text-secondary text-2xl"
            title="Back"
          >
            <HiOutlineArrowLeft />
          </button>

          <h2 className="text-2xl font-bold text-primary text-center leading-none">
            {type === ATTENDANCE_TYPES.CHECKIN ? "Check-In" : "Check-Out"} Form
          </h2>
        </div>

        {!isAdminOrHO && (
          <div className="mb-4">
            {taskCheckLoading && (
              <div className="flex justify-center">
                <div className="text-sm text-gray-500 animate-pulse">
                  Checking tasks...
                </div>
              </div>
            )}

            {!taskCheckLoading &&
              (type === ATTENDANCE_TYPES.CHECKIN
                ? hasTodayTask === false
                : taskWarning === true) && (
                <div className="flex justify-center">
                  <div className="mx-2 sm:mx-0 flex items-start gap-2 bg-yellow-100 text-yellow-800 border border-yellow-300 px-4 py-2 rounded-md text-sm font-medium shadow-sm max-w-md w-auto">
                    <RiErrorWarningFill className="w-5 h-5 flex-shrink-0 text-yellow-700 mt-0.5" />
                    <span className="flex-1">
                      {type === ATTENDANCE_TYPES.CHECKIN
                        ? TASK_WARNING
                        : INCOMPLETE_TASK_WARNING}
                    </span>
                  </div>
                </div>
              )}
          </div>
        )}

        <Formik
          initialValues={initialAttendanceValue}
          enableReinitialize
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue }) => (
            <>
              <Form className="space-y-4 text-secondary">
                <div>
                  <label className="block font-medium">Site</label>
                  <Field
                    as="select"
                    name="site"
                    disabled={values.mark_attendance_without_site}
                    className="w-full border border-gray-300 rounded px-3 py-2 mt-1 disabled:cursor-not-allowed disabled:bg-gray-50"
                    onChange={(e) => {
                      const siteId = e.target.value;
                      const site = siteOptions.find(
                        (item) => String(item.id) === String(siteId),
                      );

                      setFieldValue("site", siteId);
                      setFieldValue("mark_attendance_without_site", false);
                      setResolvedSite(site || null);

                      if (site?.geofence) {
                        setSelectedSitePolygon(parseWKTPolygon(site.geofence));
                      } else {
                        setSelectedSitePolygon(null);
                        setOutsideGeofence(false);
                      }
                    }}
                  >
                    <option value="">Select a site</option>
                    {siteOptions.map((site) => (
                      <option key={site.id} value={site.id}>
                        {site.name}
                      </option>
                    ))}
                  </Field>

                  {profile?.mark_attendance_without_site && (
                    <label className="mt-3 flex items-center gap-2 text-sm text-gray-700">
                      <Field
                        type="checkbox"
                        name="mark_attendance_without_site"
                        checked={values.mark_attendance_without_site}
                        onChange={(e) => {
                          const checked = e.target.checked;
                          setFieldValue(
                            "mark_attendance_without_site",
                            checked,
                          );

                          if (checked) {
                            setFieldValue("site", "");
                            setResolvedSite(null);
                            setSelectedSitePolygon(null);
                            setOutsideGeofence(false);
                          }
                        }}
                        className="h-4 w-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      Mark attendance without site
                    </label>
                  )}

                  {values.mark_attendance_without_site && (
                    <div className="mt-1 border border-gray-300 rounded px-3 py-2 bg-gray-50 text-sm text-gray-600">
                      Marking attendance without a site (permitted by HR).
                    </div>
                  )}
                  {resolvedSite && outsideGeofence && (
                    <div className="mt-2 flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-200 px-3 py-2 rounded">
                      <RiErrorWarningFill className="mt-0.5 shrink-0" />
                      <span>
                        You are outside your default site’s geofence. If you
                        are on site, move to an open area and refresh location.
                      </span>
                    </div>
                  )}
                  <div className="mt-2 flex justify-end">
                    <button
                      type="button"
                      onClick={refreshLocation}
                      disabled={refreshingLocation}
                      className="shrink-0 bg-primary text-white text-xs px-3 py-1 rounded hover:bg-green-700 disabled:opacity-60"
                    >
                      {refreshingLocation ? "Refreshing..." : "Refresh Location"}
                    </button>
                  </div>
                </div>

                <div className="flex justify-between items-center w-full gap-6">
                  <div className="w-5/6">
                    <label className="block font-medium">Date</label>
                    <Field
                      name="date"
                      type="date"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="date"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>

                  <div className="w-5/6">
                    <label className="block font-medium">Time</label>
                    <Field
                      name="time"
                      type="time"
                      disabled
                      className="w-full border border-gray-300 rounded px-3 py-2 mt-1 text-gray-500"
                    />
                    <ErrorMessage
                      name="time"
                      component="div"
                      className="text-red-600 text-sm"
                    />
                  </div>
                </div>
                {!isAndroid && (
                  <div className="flex flex-col sm:flex-row sm:items-center sm:flex-wrap gap-2 py-2 w-full">
                    {/* First Row */}
                    <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                      <label className="font-medium whitespace-nowrap">
                        Upload Photo
                      </label>
                      <button
                        type="button"
                        onClick={takePhoto}
                        className="bg-secondary text-white px-4 py-2 rounded hover:bg-primary"
                      >
                        Take Selfie
                      </button>
                    </div>

                    {/* Second Row */}
                    {photo && (
                      <div className="flex justify-center sm:justify-start items-center gap-4 w-full sm:w-auto">
                        <button
                          type="button"
                          className="text-primary underline flex items-center gap-1"
                          onClick={() => setShowPreview(true)}
                        >
                          <HiViewfinderCircle className="shrink-0" />
                          <span>View Selfie</span>
                        </button>
                        <p className="text-green-600 whitespace-nowrap">
                          Selfie captured ✔
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {isAndroid && (
                  <div className="flex justify-between gap-2">
                    <label className="block font-medium py-2">
                      Upload Photo
                    </label>
                    <div>
                      <CameraPreviewComponent onPhotoCapture={setPhoto} />
                    </div>
                  </div>
                )}

                {/* GOOGLE MAP CODE START */}
                {/* Map preview is optional — a missing/invalid API key or
                    network block only skips this widget, it no longer
                    blocks check-in/check-out. */}
                {loadError && (
                  <p className="mt-2 text-xs text-gray-400">
                    {MESSAGES.MAP_LOADING_ERROR} — map preview unavailable, you
                    can still continue.
                  </p>
                )}
                {isLoaded && location.latitude && location.longitude && (
                  <div className="mt-4 rounded overflow-hidden border">
                    <GoogleMap
                      mapContainerStyle={containerStyle}
                      center={{
                        lat: location.latitude,
                        lng: location.longitude,
                      }}
                      zoom={14}
                      options={{
                        gestureHandling: "none",
                        draggable: false,
                        scrollwheel: false,
                        disableDoubleClickZoom: true,
                        keyboardShortcuts: false,
                      }}
                    >
                      {/* Marker for user's current location */}
                      <Marker
                        position={{
                          lat: location.latitude,
                          lng: location.longitude,
                        }}
                      />

                      {/* Polygon for selected site geofence */}
                      {selectedSitePolygon &&
                        selectedSitePolygon.length > 0 && (
                          <Polygon
                            paths={selectedSitePolygon.map(([lng, lat]) => ({
                              lat,
                              lng,
                            }))}
                            options={{
                              fillColor: "#00BFFF",
                              fillOpacity: 0.3,
                              strokeColor: "#1E90FF",
                              strokeOpacity: 0.8,
                              strokeWeight: 2,
                            }}
                          />
                        )}
                    </GoogleMap>
                  </div>
                )}
                {/* GOOGLE MAP FINISH */}

                <div className="text-center text-sm text-red-600 font-medium">
                  <p>Time left: {formatTime(timeLeft)}</p>
                </div>

                <div className="flex justify-between mt-8">
                  {/* CSS to be done based after viewing other pages */}
                  <button
                    type="button"
                    onClick={() => navigate(BASE)}
                    className="px-4 py-2 bg-gray-300 rounded hover:bg-gray-400"
                  >
                    Cancel
                  </button>
                  <div className="relative inline-block">
                    <button
                      type="submit"
                      className="bg-primary text-white px-4 py-2 rounded hover:bg-green-700 disabled:opacity-50"
                      disabled={
                        submitting || !locationLoaded || !isLocationAvailable
                      }
                    >
                      {submitting ? "Submitting..." : "Submit"}
                    </button>

                    {!isAdminOrHO &&
                      !taskCheckLoading &&
                      ((type === ATTENDANCE_TYPES.CHECKIN && !hasTodayTask) ||
                        (type === ATTENDANCE_TYPES.CHECKOUT &&
                          taskWarning)) && (
                        <RiErrorWarningFill
                          size={25}
                          className="absolute -top-4 -right-4 text-red-500 bg-transparent"
                        />
                      )}
                  </div>
                </div>
              </Form>

              {showPreview && photo && (
                <div className="fixed inset-0 z-[9999] bg-black bg-opacity-60 flex items-center justify-center">
                  <div className="bg-white rounded-lg shadow-lg w-full max-w-lg mx-4 sm:mx-6 max-h-[90vh] overflow-auto relative">
                    <div className="flex justify-between items-center px-4 py-2 border-b">
                      <h3 className="text-lg font-semibold text-primary">
                        Selfie Preview
                      </h3>
                      <button
                        onClick={() => setShowPreview(false)}
                        className="text-red-500 font-bold text-xl hover:text-red-700"
                      >
                        &times;
                      </button>
                    </div>
                    <div className="p-4 flex justify-center">
                      <img
                        src={URL.createObjectURL(photo)}
                        alt="Selfie Preview"
                        className="max-w-full max-h-[70vh] object-contain rounded border"
                      />
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </Formik>
      </div>
    </div>
  );
};

export default AttendanceForm;
